/**
 * The data layer both clients share.
 *
 * These are ordinary React hooks over TanStack Query, which runs unchanged in
 * React and React Native — that's most of why the web app is plain React rather
 * than Next.js. Everything design-dependent stays in the apps; everything where
 * the two clients disagreeing would be a *bug* lives here.
 *
 * Two things this file is responsible for and the apps must not reimplement:
 *
 * 1. **Invalidation.** Logging, editing or deleting food changes the day's
 *    totals. Every mutation that touches an entry invalidates the day it landed
 *    on, so the number on screen can't lag the number in the database.
 * 2. **Offline-safe logging.** `client_id` is generated here, once per logical
 *    entry, so a retry after a timeout updates the same row instead of logging
 *    the meal twice.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useMemo } from "react";

import { localDate } from "./day";
import { clientId } from "./id";
import { keys, staleTime } from "./queries";
import {
  lookupBarcode,
  scanPhoto,
  type ApiClient,
  type BarcodeLookup,
  type Scan,
} from "./scan";
import type {
  AcceptedDay,
  BatchDetail,
  BatchShare,
  CookedBatch,
  CorrectionResult,
  CorrectionScope,
  FoodCorrectionEntry,
  FoodEdit,
  DailyTarget,
  DaySummary,
  FoodItem,
  FoodLog,
  IntakeCorrelation,
  LogInput,
  ManualFoodInput,
  Meal,
  Overview,
  PlannedEntry,
  Portion,
  Profile,
  ProfileUpdate,
  RecentPage,
  Recipe,
  RecipeInput,
  PotInput,
  SharedMeal,
  ShoppingList,
  UsualMeal,
  WeightEntry,
  WeightTrend,
} from "./types";

/**
 * The API client and the user's timezone, supplied by each app.
 *
 * Timezone lives here rather than being read from the device because the
 * server buckets days by the *profile's* zone. A phone that's just landed in
 * another country would otherwise ask for a day the server has already filed
 * under a different date.
 */
export interface CalorioContext {
  api: ApiClient;
  timeZone: string;
}

/* -------------------------------------------------------------------------- */
/* Profile                                                                    */
/* -------------------------------------------------------------------------- */

export function useProfile({ api }: CalorioContext): UseQueryResult<Profile> {
  return useQuery({
    queryKey: keys.profile,
    queryFn: () => api.get<Profile>("/v1/me"),
    staleTime: staleTime.profile,
  });
}

/**
 * Edit the profile — timezone, body stats, and which direction the goal points.
 *
 * Send only what changed. The endpoint applies `exclude_unset`, so an omitted
 * field keeps its stored value; sending the whole object back would turn every
 * save into a write of all six columns and make a concurrent edit from the
 * other client a silent overwrite.
 *
 * Timezone is why this invalidates more than the profile. The server buckets a
 * log into a local date at *write* time and never recomputes it, so changing
 * the zone does not move any entry — but it does change which date is "today"
 * for this user, and every cached day and overview was keyed against the old
 * answer. Dropping them is cheaper than reasoning about which ones survive.
 */
export function useUpdateProfile(
  ctx: CalorioContext,
): UseMutationResult<Profile, Error, ProfileUpdate> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (changes) => ctx.api.patch<Profile>("/v1/me", changes),
    onSuccess: (profile) => {
      client.setQueryData(keys.profile, profile);
      client.invalidateQueries({ queryKey: ["day"] });
      client.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* The day                                                                    */
/* -------------------------------------------------------------------------- */

/** One local day. Pass a `YYYY-MM-DD`, or omit for today in the user's zone. */
export function useDay(
  ctx: CalorioContext,
  date?: string,
): UseQueryResult<DaySummary> & { date: string } {
  const day = date ?? localDate(ctx.timeZone);
  const query = useQuery({
    queryKey: keys.day(day),
    queryFn: () => ctx.api.get<DaySummary>(`/v1/days/${day}`),
    staleTime: staleTime.day,
  });
  return useMemo(() => ({ ...query, date: day }), [query, day]);
}

/**
 * Log food.
 *
 * The `client_id` is minted here per call, so the mutation is idempotent on the
 * server: a retry after a network timeout updates the same row rather than
 * duplicating the meal. TanStack Query's own retries reuse the variables it was
 * given, which is exactly what makes this work.
 */
export function useLogFood(ctx: CalorioContext): UseMutationResult<FoodLog, Error, LogInput> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: LogInput) =>
      ctx.api.post<FoodLog>("/v1/logs", { ...input, client_id: clientId() }),
    onSuccess: (entry) => {
      // Invalidate the day the entry actually landed on, which is not
      // necessarily today — users backfill yesterday's dinner.
      client.invalidateQueries({ queryKey: keys.day(entry.local_date) });
      client.invalidateQueries({ queryKey: ["logs"] });
      // The weight just written is now one of this food's habits.
      client.invalidateQueries({ queryKey: ["food", "portions"] });
    },
  });
}

export function useUpdateLog(
  ctx: CalorioContext,
): UseMutationResult<FoodLog, Error, { id: string; changes: Partial<FoodLog> }> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, changes }) => ctx.api.patch<FoodLog>(`/v1/logs/${id}`, changes),
    onSuccess: (entry, { changes }) => {
      client.invalidateQueries({ queryKey: keys.day(entry.local_date) });
      // Moving an entry to another day changes two days, not one. Missing this
      // leaves the old day showing food that is no longer on it.
      if (changes.local_date && changes.local_date !== entry.local_date) {
        client.invalidateQueries({ queryKey: keys.day(changes.local_date) });
      }
      // A re-weigh moves a count from one habitual weight to another.
      if (changes.quantity_g !== undefined) {
        client.invalidateQueries({ queryKey: ["food", "portions"] });
      }
    },
  });
}

export function useDeleteLog(
  ctx: CalorioContext,
): UseMutationResult<void, Error, { id: string; localDate: string }> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id }) => ctx.api.delete<void>(`/v1/logs/${id}`),
    onSuccess: (_, { localDate: day }) => {
      client.invalidateQueries({ queryKey: keys.day(day) });
      client.invalidateQueries({ queryKey: ["logs"] });
      client.invalidateQueries({ queryKey: ["food", "portions"] });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Finding food                                                               */
/* -------------------------------------------------------------------------- */

export function useFoodSearch(
  { api }: CalorioContext,
  query: string,
): UseQueryResult<FoodItem[]> {
  return useQuery({
    queryKey: keys.foodSearch(query),
    queryFn: () => api.get<FoodItem[]>(`/v1/foods/search?q=${encodeURIComponent(query)}`),
    // The endpoint requires two characters; asking sooner is a guaranteed 422.
    enabled: query.trim().length >= 2,
    staleTime: staleTime.food,
  });
}


/* -------------------------------------------------------------------------- */
/* The quick tabs — spec page 03                                              */
/* -------------------------------------------------------------------------- */

/**
 * How Recent is ordered — `"recent"`, or one of the four figures.
 *
 * Written out here rather than taken from the generated schema because it is a
 * *query* parameter, and `openapi-typescript` only gives us response shapes. It
 * has to match `RecentSort` in `api/v1/quick.py`, which refuses anything it
 * does not recognise rather than quietly answering with the clock.
 */
export type RecentSort = "recent" | "kcal" | "protein" | "carbs" | "fat";

/**
 * The URL both Recent hooks ask for.
 *
 * `meal` and `sort` are only sent when they say something — a bare
 * `/v1/foods/recent` is the request this endpoint has always answered, and the
 * default sort is the ordering it already had, so spelling it out would be a
 * different URL for an identical question.
 */
function recentUrl(
  meal: Meal | null,
  sort: RecentSort,
  page?: { limit: number; offset: number },
): string {
  const parts: string[] = [];
  if (page) parts.push(`limit=${page.limit}`, `offset=${page.offset}`);
  if (meal) parts.push(`meal=${meal}`);
  if (sort !== "recent") parts.push(`sort=${sort}`);
  return `/v1/foods/recent${parts.length ? `?${parts.join("&")}` : ""}`;
}

/**
 * How many rows a page of Recent holds.
 *
 * Enough to overflow a 560px sheet's list area — a page that fits without
 * scrolling never triggers the fetch that would bring the next one, and the
 * list would look finished at twenty rows forever.
 */
export const RECENT_PAGE_SIZE = 25;

/**
 * Recent, one page at a time, for a list that scrolls.
 *
 * The offset is derived from what has already arrived rather than from a page
 * counter, so a short page — the server returning fewer rows than asked for,
 * which it may — cannot leave the cursor pointing past the gap it left.
 *
 * `has_more` is the server's answer and the only thing that ends the list.
 * Stopping when a page comes back short would end it early on any page the
 * visibility filter trimmed.
 *
 * The meal orders the whole list, not just the page in hand, which is why it
 * goes to the server rather than being sorted here: page two of a list sorted
 * client-side is page two of the wrong list.
 */
export function useRecentPages(
  { api }: CalorioContext,
  meal: Meal | null = null,
  pageSize = RECENT_PAGE_SIZE,
  sort: RecentSort = "recent",
): UseInfiniteQueryResult<{ pages: RecentPage[]; pageParams: number[] }, Error> {
  return useInfiniteQuery({
    queryKey: [...keys.recentPages(meal, sort), pageSize],
    queryFn: ({ pageParam }) =>
      api.get<RecentPage>(recentUrl(meal, sort, { limit: pageSize, offset: pageParam })),
    initialPageParam: 0,
    getNextPageParam: (last, all) =>
      last.has_more ? all.reduce((n, page) => n + page.items.length, 0) : undefined,
    staleTime: staleTime.day,
  });
}

/**
 * The platefuls this meal keeps coming back to, at the weights it last used.
 *
 * Per meal, because that is the question the add sheet is open on. One list for
 * the whole log puts porridge at the top of it at seven in the evening, which
 * is how a "fast path" becomes a thing people scroll past.
 *
 * A meal per row rather than a food, which is what keeps this from being Recent
 * with a second sort on it — see `UsualMeal`.
 */
export function useUsuals(
  { api }: CalorioContext,
  meal: Meal | null,
): UseQueryResult<UsualMeal[]> {
  return useQuery({
    queryKey: keys.usuals(meal ?? ""),
    queryFn: () => api.get<UsualMeal[]>(`/v1/foods/usuals?meal=${meal}`),
    // Null is a closed sheet. Asking for the usuals of no meal is a 422.
    enabled: meal !== null,
    // A habit does not turn over on one entry, but the portions on the tile are
    // the last ones used — so this follows the day rather than the food.
    staleTime: staleTime.day,
  });
}

export function useFavourites({ api }: CalorioContext): UseQueryResult<FoodItem[]> {
  return useQuery({
    queryKey: keys.favourites,
    queryFn: () => api.get<FoodItem[]>("/v1/foods/favourites"),
    staleTime: staleTime.food,
  });
}

/**
 * Star and unstar. Both idempotent server-side, which is what lets the optimistic
 * update below be a plain overwrite rather than a toggle — a double-tap can't
 * desynchronise the cache from the server.
 */
export function useSetFavourite(
  ctx: CalorioContext,
): UseMutationResult<void, Error, { foodId: string; favourite: boolean }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ foodId, favourite }) =>
      favourite
        ? ctx.api.put<void>(`/v1/foods/${foodId}/favourite`, {})
        : ctx.api.delete<void>(`/v1/foods/${foodId}/favourite`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.favourites }),
  });
}

/**
 * Create a food by hand.
 *
 * The third way a food enters the system, after a barcode lookup and a label
 * photo — and the only one that always works. Someone cooking from a family
 * recipe has no barcode to scan and no panel to photograph, and OCR that cannot
 * read a smudged label is a dead end without this.
 *
 * `source: "user"` is not a parameter. The server keeps manually entered foods
 * private to their author and refuses a barcode on them, because one person's
 * typo would otherwise become every future scanner's cache hit — so there is
 * nothing for a caller to decide, and a caller that could decide it would
 * eventually decide it wrong.
 */
/**
 * Several food items by id, as a lookup.
 *
 * The recipe editor's problem: `RecipeOut.items` carries `food_item_id` and
 * grams, not the food itself, so an existing recipe has ingredient rows with
 * nothing to name them. One query per ingredient rather than a batch endpoint
 * because each is an indexed primary-key read that `keys.food` already caches
 * for five minutes — a recipe being edited is a handful of them, once, and
 * most will already be in cache from the search that put them there.
 *
 * `combine` folds the results into one object so a caller re-renders on the set
 * rather than on each arrival.
 */
/**
 * One food by id, when there is one to fetch.
 *
 * `null` disables it rather than the caller branching around the hook, because
 * a hook cannot be called conditionally — and the caller here is a row that
 * only needs the food once somebody asks to correct it.
 */
export function useFood({ api }: CalorioContext, id: string | null): UseQueryResult<FoodItem> {
  return useQuery({
    queryKey: keys.food(id ?? ""),
    queryFn: () => api.get<FoodItem>(`/v1/foods/${id}`),
    enabled: Boolean(id),
    staleTime: staleTime.food,
  });
}

/**
 * The weights this person has logged one food at, most habitual first.
 *
 * Asked for when a pad opens and not before: the chips are the only reader,
 * and a list of every food's habits fetched alongside Recent would be a
 * request per row for a number most rows never need. `null` while nothing is
 * on the pad, so the hook can sit in a component that is usually not weighing
 * anything.
 *
 * Follows the day rather than the food, because a write is what changes it —
 * and the queue marks it stale rather than refetching it, so a chip cannot
 * move under a thumb that is already on its way to it.
 */
export function useFoodPortions(
  { api }: CalorioContext,
  foodId: string | null,
): UseQueryResult<Portion[]> {
  return useQuery({
    queryKey: keys.portions(foodId ?? ""),
    queryFn: () => api.get<Portion[]>(`/v1/foods/${foodId}/portions`),
    enabled: Boolean(foodId),
    staleTime: staleTime.day,
  });
}

export function useFoods(
  { api }: CalorioContext,
  ids: readonly string[],
): { byId: Record<string, FoodItem>; isLoading: boolean } {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: keys.food(id),
      queryFn: () => api.get<FoodItem>(`/v1/foods/${id}`),
      staleTime: staleTime.food,
    })),
    combine: (results) => ({
      byId: Object.fromEntries(
        results.flatMap((result) => (result.data ? [[result.data.id, result.data]] : [])),
      ),
      isLoading: results.some((result) => result.isLoading),
    }),
  });
}

export function useCreateFood(
  ctx: CalorioContext,
): UseMutationResult<FoodItem, Error, ManualFoodInput> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input) =>
      ctx.api.post<FoodItem>("/v1/foods", {
        ...input,
        source: "user",
        basis_unit: input.basis_unit ?? "g",
        servings: input.servings ?? [],
      }),
    onSuccess: () => {
      // Search results and recents both change: the food is now findable, and
      // it belongs at the top of the list of things this person actually eats.
      client.invalidateQueries({ queryKey: ["food"] });
      client.invalidateQueries({ queryKey: keys.recent });
    },
  });
}

/**
 * The spec's "Logged this time last week".
 *
 * Returns log *entries*, not foods, because the portion is the useful part —
 * you probably had the same 140 g of salmon, not just salmon.
 */
export function useLoggedLastWeek(
  { api }: CalorioContext,
  daysAgo = 7,
): UseQueryResult<FoodLog[]> {
  return useQuery({
    queryKey: keys.lastWeek(daysAgo),
    queryFn: () => api.get<FoodLog[]>(`/v1/logs/this-time-last-week?days_ago=${daysAgo}`),
    staleTime: staleTime.food,
  });
}

/*
 * Calories with no food behind them used to have a hook here, and does not any
 * more.
 *
 * `useQuickAdd` posted straight to `/v1/logs/quick`, around the offline queue —
 * so the one kind of entry most often typed in a restaurant, on a phone with no
 * signal, was the one kind that could fail silently. Both clients now go
 * through `offline.quickAdd`, which queues it like every other write and shows
 * the row immediately. The hook had no caller left, which is what `reach.test`
 * is for.
 */

/* -------------------------------------------------------------------------- */
/* Scanning                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Barcode → food item, no photograph involved.
 *
 * A mutation over a GET, deliberately. The camera drives this, not a render:
 * the decoder hands over a string at some unpredictable moment and the screen
 * needs `isPending` and an imperative `mutateAsync` to sequence against, which
 * is a mutation's shape and not a query's. There is nothing to cache here
 * either — the scanning loop already refuses to ask twice about the same code.
 *
 * A miss resolves rather than throwing, carrying the server's own sentence. See
 * `lookupBarcode`: a code we don't have is a normal thing to see through a
 * viewfinder, and a screen that treats it as an error stops scanning at exactly
 * the moment the user wants to carry on aiming.
 */
export function useLookupBarcode(
  ctx: CalorioContext,
): UseMutationResult<BarcodeLookup, Error, string> {
  return useMutation({
    mutationFn: (barcode: string) => lookupBarcode(ctx.api, barcode),
    // The loop will come round again on the next frame if this was a blip,
    // which is a better retry than one we schedule ourselves.
    retry: false,
  });
}

/**
 * Photograph → food item.
 *
 * The label path. Barcodes no longer come through here to be *resolved* — that
 * is `useLookupBarcode`, which answers without an upload — but pass the last
 * one decoded anyway: the server writes it onto whatever OCR parses, so a
 * product Open Food Facts has never heard of is a cache hit for the next
 * person who scans it.
 */
export function useScanPhoto(
  ctx: CalorioContext,
): UseMutationResult<Scan, Error, { image: Blob; barcode?: string }> {
  return useMutation({
    mutationFn: ({ image, barcode }) => scanPhoto(ctx.api, image, { barcode }),
    // The upload and the poll are already retried internally where it makes
    // sense; retrying the whole flow would re-upload the photo and redo the OCR
    // for no benefit.
    retry: false,
  });
}


/**
 * Correct a wrong reading.
 *
 * `shared` in the result tells you whether the edit changed the row everyone
 * sees or forked a private copy — surface those differently. Silently applying
 * a global edit is alarming; silently making a private one is confusing when
 * the user expected to fix the database.
 */
export function useCorrectFood(
  ctx: CalorioContext,
): UseMutationResult<
  CorrectionResult,
  Error,
  { id: string; changes: FoodEdit; scope?: CorrectionScope }
> {
  const client = useQueryClient();

  return useMutation({
    // `scope` says who the fix is for. Omitting it keeps the old behaviour —
    // the source decides — which is the right default and a bad silent rule:
    // somebody who meant "only mine" and got "everyone's" has no way to know.
    mutationFn: ({ id, changes, scope }) =>
      ctx.api.patch<CorrectionResult>(`/v1/foods/${id}/correction`, { ...changes, ...(scope ? { scope } : {}) }),
    onSuccess: () => {
      // Past log entries keep their snapshot by design, so days are untouched —
      // only food lookups need refreshing.
      client.invalidateQueries({ queryKey: ["food"] });
      client.invalidateQueries({ queryKey: ["corrections"] });
    },
  });
}

/**
 * Who changed what on a shared food.
 *
 * A row anyone can edit has to be a row anyone can inspect, or "wiki-style" is
 * just "silently mutable". The history says whether each change was yours and
 * deliberately does not name anybody else — that is enough to tell whether a
 * figure you doubt is one you wrote, without turning a data-quality log into a
 * record of what strangers eat.
 */
export function useCorrections(
  { api }: CalorioContext,
  foodId: string | null,
): UseQueryResult<FoodCorrectionEntry[]> {
  return useQuery({
    queryKey: keys.corrections(foodId ?? ""),
    queryFn: () => api.get<FoodCorrectionEntry[]>(`/v1/foods/${foodId}/corrections`),
    enabled: Boolean(foodId),
    staleTime: staleTime.food,
  });
}

/* -------------------------------------------------------------------------- */
/* Progress — streak, goal, intake series                                     */
/* -------------------------------------------------------------------------- */

/**
 * Everything spec page 01 shows above the meals list.
 *
 * One request rather than three, because the Overview's promise is answering
 * "how is the diet going" *without a tap* — three parallel queries would mean
 * three independent spinners and a screen assembling itself in front of you.
 */
export function useOverview(
  { api }: CalorioContext,
  days = 30,
): UseQueryResult<Overview> {
  return useQuery({
    queryKey: keys.overview(days),
    queryFn: () => api.get<Overview>(`/v1/overview?days=${days}`),
    // Moves whenever anything is logged, same as the day itself.
    staleTime: staleTime.day,
  });
}

export function useSetGoal(
  ctx: CalorioContext,
): UseMutationResult<unknown, Error, {
  target_weight_kg: number;
  target_date?: string;
  target_rate_kg_per_week?: number;
}> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => ctx.api.put("/v1/goal", body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["overview"] });
      client.invalidateQueries({ queryKey: ["goal"] });
    },
  });
}

/**
 * Set the daily calorie and macro budget — the number the ring fills toward.
 *
 * Effective-dated server-side: it applies from `effective_from` (the user's
 * today when omitted) forward, and finished days keep the budget they were
 * scored against. So changing it invalidates every cached day rather than just
 * today's — future days the user has already opened carry the target too.
 */
export function useSetDailyTarget(
  ctx: CalorioContext,
): UseMutationResult<DailyTarget, Error, {
  kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  effective_from?: string;
}> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => ctx.api.put<DailyTarget>("/v1/targets", body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["day"] });
      // The overview's "vs target" figures read the same budget.
      client.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

/**
 * The spec's "Against intake" panel.
 *
 * Check `weeks_under` and `weeks_over` before drawing it — a comparison built
 * on two weeks of data reads as a finding when it's noise.
 */
export function useIntakeCorrelation(
  { api }: CalorioContext,
  weeks = 12,
): UseQueryResult<IntakeCorrelation> {
  return useQuery({
    queryKey: keys.correlation(weeks),
    queryFn: () => api.get<IntakeCorrelation>(`/v1/weight/correlation?weeks=${weeks}`),
    staleTime: staleTime.weight,
  });
}

/* -------------------------------------------------------------------------- */
/* Weight                                                                     */
/* -------------------------------------------------------------------------- */

export function useWeightTrend(
  { api }: CalorioContext,
  days = 90,
): UseQueryResult<WeightTrend> {
  return useQuery({
    queryKey: keys.weight(days),
    queryFn: () => api.get<WeightTrend>(`/v1/weight?days=${days}`),
    staleTime: staleTime.weight,
  });
}

export function useRecordWeight(
  ctx: CalorioContext,
): UseMutationResult<WeightEntry, Error, { weight_kg: number; local_date?: string; note?: string }> {
  const client = useQueryClient();

  return useMutation({
    // PUT, not POST: one reading per day, so weighing again corrects the
    // morning's entry rather than adding a second. The backend registers only
    // PUT here, so calling post() answered 405 and the weigh-in was silently
    // dropped — on both clients.
    mutationFn: (body) => ctx.api.put<WeightEntry>("/v1/weight", body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["weight"] });
      // The profile carries the latest weight, derived from this series.
      client.invalidateQueries({ queryKey: keys.profile });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Recipes                                                                    */
/* -------------------------------------------------------------------------- */

export function useRecipes({ api }: CalorioContext): UseQueryResult<Recipe[]> {
  return useQuery({
    queryKey: keys.recipes,
    queryFn: () => api.get<Recipe[]>("/v1/recipes"),
    staleTime: staleTime.recipes,
  });
}

/**
 * Create a recipe, or replace one that exists.
 *
 * One hook for both because the endpoints take the same body and do the same
 * work — `PUT` differs from `POST` only in reusing the row. Splitting them
 * would mean the editor deciding which mutation object to call as well as
 * which id to send, for no gain.
 *
 * Saving rebuilds the recipe's materialised food item server-side, so this
 * invalidates far more than the recipe list. That food is an ordinary
 * `food_items` row: it shows up in search, in recents, and — if the user has
 * starred it — in favourites, all with nutrition that has just changed.
 * Entries already logged keep their frozen snapshot, which is why the day is
 * *not* invalidated: nothing about a past meal moved.
 */
export function useSaveRecipe(
  ctx: CalorioContext,
): UseMutationResult<Recipe, Error, { id?: string; input: RecipeInput }> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }) =>
      id
        ? ctx.api.put<Recipe>(`/v1/recipes/${id}`, input)
        : ctx.api.post<Recipe>("/v1/recipes", input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.recipes });
      client.invalidateQueries({ queryKey: ["food"] });
    },
  });
}

/**
 * Delete a recipe.
 *
 * The materialised food survives on the server by design, so a diary entry
 * from last month still names a real food rather than a dangling reference —
 * which is also why `["food"]` is invalidated rather than assumed gone.
 */
export function useDeleteRecipe(ctx: CalorioContext): UseMutationResult<void, Error, string> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id) => ctx.api.delete<void>(`/v1/recipes/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.recipes });
      client.invalidateQueries({ queryKey: ["food"] });
    },
  });
}


/* -------------------------------------------------------------------------- */
/* Planning ahead                                                             */
/* -------------------------------------------------------------------------- */

/** The planned entries in a date range, flat and ordered by day then meal. */
export function usePlan(
  { api }: CalorioContext,
  from: string,
  to: string,
): UseQueryResult<PlannedEntry[]> {
  return useQuery({
    queryKey: keys.plan(from, to),
    queryFn: () => api.get<PlannedEntry[]>(`/v1/plan?from_date=${from}&to_date=${to}`),
    staleTime: staleTime.day,
  });
}

/**
 * Put one planned entry, at an id the *client* chooses.
 *
 * The id is minted here rather than by the server, and that is load-bearing
 * rather than a preference: accepting a day derives each log's idempotency key
 * from the planned entry's id, so a server-minted id would break the chain at
 * its first link — the client would have nothing stable to retry against until
 * a round trip had already succeeded.
 */
export function useSavePlannedEntry(
  ctx: CalorioContext,
): UseMutationResult<
  PlannedEntry,
  Error,
  {
    id?: string;
    local_date: string;
    meal?: Meal;
    food_item_id?: string;
    recipe_id?: string;
    batch_id?: string;
    quantity_g?: number;
    portions?: number;
  }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) =>
      ctx.api.put<PlannedEntry>(`/v1/plan/entries/${id ?? clientId()}`, body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["plan"] });
    },
  });
}

export function useDeletePlannedEntry(
  ctx: CalorioContext,
): UseMutationResult<void, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ctx.api.delete<void>(`/v1/plan/entries/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["plan"] });
    },
  });
}

/**
 * Turn a day's plan into diary entries.
 *
 * Answers per entry rather than pass/fail, because the interesting outcome is
 * the partial one — three writes, two landed — and a caller has to be able to
 * name the one that did not. Safe to call again: each log's `client_id` is
 * derived from its planned entry, so a retry updates the same rows rather than
 * logging the meal twice.
 */
export function useAcceptDay(
  ctx: CalorioContext,
): UseMutationResult<AcceptedDay, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (day: string) => ctx.api.post<AcceptedDay>(`/v1/plan/days/${day}/accept`, {}),
    retry: false,
    onSuccess: (result) => {
      client.invalidateQueries({ queryKey: ["plan"] });
      client.invalidateQueries({ queryKey: keys.day(result.local_date) });
      client.invalidateQueries({ queryKey: ["overview"] });
      client.invalidateQueries({ queryKey: keys.batches });
    },
  });
}

/** Copy the week seven days back onto this one. Skips days already planned. */
export function useFillFromLastWeek(
  ctx: CalorioContext,
): UseMutationResult<PlannedEntry[], Error, { from_date: string; to_date: string }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (range) => ctx.api.post<PlannedEntry[]>("/v1/plan/fill-from-last-week", range),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["plan"] });
    },
  });
}

export function useShoppingList(
  { api }: CalorioContext,
  from: string,
  to: string,
): UseQueryResult<ShoppingList> {
  return useQuery({
    queryKey: keys.shopping(from, to),
    queryFn: () => api.get<ShoppingList>(`/v1/plan/shopping?from_date=${from}&to_date=${to}`),
    staleTime: staleTime.day,
  });
}

/* -------------------------------------------------------------------------- */
/* Leftovers                                                                  */
/* -------------------------------------------------------------------------- */

/** What is in the fridge: open batches only. */
export function useBatches({ api }: CalorioContext): UseQueryResult<CookedBatch[]> {
  return useQuery({
    queryKey: keys.batches,
    queryFn: () => api.get<CookedBatch[]>("/v1/batches"),
    staleTime: staleTime.day,
  });
}

/** It went in the bin. Keeps `portions_remaining` as the record of the waste. */
export function useDiscardBatch(
  ctx: CalorioContext,
): UseMutationResult<CookedBatch, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ctx.api.post<CookedBatch>(`/v1/batches/${id}/discard`, {}),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.batches }),
  });
}

/**
 * One pot, opened — what went in, and what has been shared of it.
 *
 * A separate query from the list rather than a filter over it, because the list
 * is the *open* pots and this is a particular one somebody asked for. Opening a
 * pot that has since been finished has to answer with the pot, not with
 * nothing.
 */
export function useBatch(
  { api }: CalorioContext,
  id: string | null,
): UseQueryResult<BatchDetail> {
  return useQuery({
    queryKey: keys.batch(id ?? "none"),
    queryFn: () => api.get<BatchDetail>(`/v1/batches/${id}`),
    enabled: id != null,
    staleTime: staleTime.day,
  });
}

/**
 * Cook a pot, from a saved meal or from what actually went in the pan.
 *
 * It carries both forms: a saved recipe cooked as written, and a composition
 * with no recipe behind it yet, along with the weight the pot came out of the
 * pan at.
 */
export function useCookPot(
  ctx: CalorioContext,
): UseMutationResult<CookedBatch, Error, PotInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => ctx.api.post<CookedBatch>("/v1/batches", body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.batches });
      // A pot built here writes a recipe, saved or not, and one saved here is a
      // new entry in the meals the sheet offers.
      client.invalidateQueries({ queryKey: keys.recipes });
    },
  });
}

/**
 * Change what a pot turned out to be: the name, the count, the weight, the date.
 *
 * The name is the pot's own and never the recipe's — see the server's
 * `app/models/batch.py`. An empty string is how the caller says "call it
 * whatever the recipe is called again"; omitting the field leaves it alone.
 *
 * Both the list and the pot's own page are dropped, because a rename made on
 * one of them is a lie on the other until it refetches.
 */
export function useUpdateBatch(
  ctx: CalorioContext,
): UseMutationResult<
  BatchDetail,
  Error,
  {
    id: string;
    name?: string;
    portions_made?: number;
    cooked_weight_g?: number;
    best_before?: string;
  }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => ctx.api.patch<BatchDetail>(`/v1/batches/${id}`, body),
    onSuccess: (batch) => {
      client.invalidateQueries({ queryKey: keys.batches });
      client.invalidateQueries({ queryKey: keys.batch(batch.id) });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Sharing                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Open a link on a pot.
 *
 * The switches are sent rather than defaulted client-side, because the composer
 * shows them before the link exists and what it shows has to be what is made.
 */
export function useCreateShare(
  ctx: CalorioContext,
): UseMutationResult<
  BatchShare,
  Error,
  {
    batchId: string;
    include_ingredients?: boolean;
    include_batch?: boolean;
    expires_in_days?: number | null;
  }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, ...body }) =>
      ctx.api.post<BatchShare>(`/v1/batches/${batchId}/shares`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.batches }),
  });
}

/**
 * Change a link that is already out there.
 *
 * Live on purpose: the person holding it sees the new answer the next time they
 * open it. A composer that previewed one page and had shared another would be
 * lying about the only thing it exists to show.
 */
export function useUpdateShare(
  ctx: CalorioContext,
): UseMutationResult<
  BatchShare,
  Error,
  {
    id: string;
    include_ingredients?: boolean;
    include_batch?: boolean;
    expires_in_days?: number;
    never_expires?: boolean;
  }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => ctx.api.patch<BatchShare>(`/v1/shares/${id}`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.batches }),
  });
}

/** Close a link. The row stays, so the list can still answer for it. */
export function useRevokeShare(
  ctx: CalorioContext,
): UseMutationResult<BatchShare, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ctx.api.post<BatchShare>(`/v1/shares/${id}/revoke`, {}),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.batches }),
  });
}

/**
 * The page at `/m/<token>`, for whoever opened it.
 *
 * The only query in the app that runs without a session, so it must not retry
 * into the sign-in flow: a 404 is a link that never existed and a 410 is one
 * that has been closed, and both are answers to render rather than failures to
 * recover from.
 */
export function useSharedMeal(
  { api }: CalorioContext,
  token: string | null,
): UseQueryResult<SharedMeal> {
  return useQuery({
    queryKey: keys.sharedMeal(token ?? "none"),
    queryFn: () => api.get<SharedMeal>(`/v1/shared/${token}`),
    enabled: token != null,
    retry: false,
    staleTime: staleTime.food,
  });
}

/**
 * Log a portion of somebody else's pot into your own day.
 *
 * It does not touch the sender's count, and nothing here pretends otherwise:
 * there is no batch to invalidate, because none of ours changed.
 */
export function useLogSharedMeal(
  ctx: CalorioContext,
  token: string,
): UseMutationResult<FoodLog, Error, { meal: Meal; portions?: number; quantity_g?: number }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      ctx.api.post<FoodLog>(`/v1/shared/${token}/log`, { ...body, client_id: clientId() }),
    onSuccess: (log) => {
      client.invalidateQueries({ queryKey: keys.day(log.local_date) });
      client.invalidateQueries({ queryKey: ["overview"] });
      client.invalidateQueries({ queryKey: keys.recent });
    },
  });
}

/** Keep the recipe you were sent, as your own copy of it. */
export function useSaveSharedRecipe(
  ctx: CalorioContext,
  token: string,
): UseMutationResult<Recipe, Error, void> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => ctx.api.post<Recipe>(`/v1/shared/${token}/save`, {}),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.recipes }),
  });
}
